// Multi-screen tab state (installed/standalone program mode) — browser-like tabs.
// Navigation updates the ACTIVE tab's URL (like following a link in a browser);
// the "+" button opens a brand-new tab (default: dashboard). Persisted so the open
// set survives an app relaunch.
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { IconName } from "../shared/Icon";

export type TabMeta = { url: string; title: string; icon: IconName; color: string };
export type Tab = TabMeta & { id: string };

type TabsState = {
  tabs: Tab[];
  activeId: string | null;
  /** Reflect the current location into the active tab (creates the first tab if none). */
  syncActive: (m: TabMeta) => void;
  /** Always open a brand-new tab and focus it. */
  openNew: (m: TabMeta) => void;
  setActive: (id: string) => void;
  /** Remove a tab; returns the URL to navigate to (neighbour) or "" to stay. */
  close: (id: string) => string;
  /** Drag-reorder: move the dragged tab to the dropped tab's position. */
  reorder: (fromId: string, toId: string) => void;
};

let _seq = 0;
const uid = () => `tab_${Date.now().toString(36)}_${(_seq++).toString(36)}`;

export const useTabs = create<TabsState>()(
  persist(
    (set, get) => ({
      tabs: [],
      activeId: null,

      syncActive: (m) => {
        const { tabs, activeId } = get();
        const active = tabs.find((t) => t.id === activeId);
        if (!active) {
          const tab: Tab = { id: uid(), ...m };
          set({ tabs: [...tabs, tab], activeId: tab.id });
        } else {
          set({ tabs: tabs.map((t) => (t.id === activeId ? { ...t, ...m } : t)) });
        }
      },

      openNew: (m) => {
        const tab: Tab = { id: uid(), ...m };
        set((s) => ({ tabs: [...s.tabs, tab], activeId: tab.id }));
      },

      setActive: (id) => set({ activeId: id }),

      close: (id) => {
        const { tabs, activeId } = get();
        const idx = tabs.findIndex((t) => t.id === id);
        if (idx < 0) return "";
        const next = tabs.filter((t) => t.id !== id);
        let navUrl = "";
        let nextActive = activeId;
        if (activeId === id) {
          const neighbour = next[idx] ?? next[idx - 1] ?? null;
          nextActive = neighbour?.id ?? null;
          navUrl = neighbour?.url ?? "/dashboard";
        }
        set({ tabs: next, activeId: nextActive });
        return navUrl;
      },

      reorder: (fromId, toId) => {
        const { tabs } = get();
        const from = tabs.findIndex((t) => t.id === fromId);
        const to = tabs.findIndex((t) => t.id === toId);
        if (from < 0 || to < 0 || from === to) return;
        const next = [...tabs];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        set({ tabs: next });
      },
    }),
    { name: "t-tabs-v2" },
  ),
);
